import type { DevelopmentDetailDataModel } from '@/domain/models/development.js';
import {
    isDevelopersSettableStatus,
    isEstimateSubmittableStatus,
    isFullEditStatus,
    isTechLeadAssignableStatus,
    isTerminalStatus
} from '@/presentation/pages/developments/development-detail/utils/development-status-rules.js';

export type TabVisibility = {
    canEditDev: boolean;
    canLinkDevNow: boolean;
    fullEditStatus: boolean;
    showEstimateForm: boolean;
    functionalEstimateIsComplete: boolean;
    showTechLeadCard: boolean;
    canEditDevelopersNow: boolean;
    hasTechLead: boolean;
    showReturnToAnalysis: boolean;
    canSubmitEstimateNow: boolean;
    showFactoryEstimateForm: boolean;
};

export function resolveTabVisibility(params: {
    dev: DevelopmentDetailDataModel;
    isCf: boolean;
    canAssignTechLead: boolean;
    canEditDevelopers: boolean;
    canReturnEstimateToAnalysis: boolean;
    canSubmitEstimate: boolean;
    canLinkDev: boolean;
}): TabVisibility {
    const { dev, isCf, canAssignTechLead, canEditDevelopers, canReturnEstimateToAnalysis, canSubmitEstimate, canLinkDev } = params;

    const canEditDev = isCf && !isTerminalStatus(dev.status);
    const fullEditStatus = isFullEditStatus(dev.status);
    // GPA/GPC/LT vinculam via a mesma tela de edição, restritos ao campo de vínculo — o gate de
    // status é o mesmo "conforme status" do CF (canBeFullyEdited() no backend).
    const canLinkDevNow = canLinkDev && fullEditStatus;
    const showEstimateForm = !dev.currentEstimate || dev.currentEstimate.rejected;
    const functionalEstimateIsComplete = dev.functionalComplexity !== null && dev.functionalHours !== null;
    const showTechLeadCard = canAssignTechLead && isTechLeadAssignableStatus(dev.status);
    const canEditDevelopersNow = canEditDevelopers && isDevelopersSettableStatus(dev.status);
    // Nada de estimativa (nem devolução de EF) aparece antes da atribuição do Líder Técnico: até lá
    // a única ação da aba é atribuí-lo.
    const hasTechLead = dev.liderTecnicoId !== null;
    const showReturnToAnalysis = canReturnEstimateToAnalysis && hasTechLead && showEstimateForm && dev.status === 'EM_ANALISE_TECNICA';
    const canSubmitEstimateNow = canSubmitEstimate && isEstimateSubmittableStatus(dev.status);
    // "Estimativa – Fábrica de Software" só aparece quando há informação: uma estimativa já registrada
    // ou o formulário do LT no momento em que ele pode de fato submeter.
    const showFactoryEstimateForm = showEstimateForm && canSubmitEstimateNow;

    return {
        canEditDev,
        canLinkDevNow,
        fullEditStatus,
        showEstimateForm,
        functionalEstimateIsComplete,
        showTechLeadCard,
        canEditDevelopersNow,
        hasTechLead,
        showReturnToAnalysis,
        canSubmitEstimateNow,
        showFactoryEstimateForm
    };
}
